import { sendToastMessage } from "@microbeta/ui";
import type React from "react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getGenericSignedUploadUrl,
  uploadFileToGCS,
} from "../../../services/uploads";
import { FileDropzone } from "../FileDropZone";
import { ImageCropper } from "./components/ImageCropper";

// Define ConceptType directly to avoid deep type instantiation issues
type ConceptType =
  | "areas"
  | "areas"
  | "models"
  | "routes"
  | "general"
  | "profile_image";

interface ImageUploaderProps {
  conceptType: ConceptType;
  conceptId: string; // UUID of the parent concept (e.g., area ID, area ID)
  onUploadComplete: (path: string) => void;
  label?: string;
  className?: string;
  disabled?: boolean;
  // Image-specific props
  maxWidth?: number;
  maxHeight?: number;
  aspectRatio?: number; // e.g., 16/9, 1 for square
  quality?: number; // 0.1 to 1.0
}

export const ImageUploader: React.FC<ImageUploaderProps> = ({
  conceptType,
  conceptId,
  onUploadComplete,
  label,
  className = "",
  disabled = false,
  maxWidth = 1920,
  maxHeight = 1080,
  aspectRatio,
  quality = 0.9,
}) => {
  const { t } = useTranslation();
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [showCropDialog, setShowCropDialog] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);

  // Custom validation function that allows larger files - we'll handle validation in handleFileSelect
  const customValidateFile = useCallback((file: File): boolean => {
    // Allow all images through, we'll do validation in handleFileSelect to show proper toast messages
    return file.type.startsWith("image/");
  }, []);

  const handleFileSelect = useCallback(
    (file: File) => {
      if (!conceptId) {
        const errorMessage = t("imageUploader.conceptIdMissing");
        sendToastMessage(t("imageUploader.errorTitle"), errorMessage, "error");
        return;
      }

      // Check if it's an image
      if (!file.type.startsWith("image/")) {
        const errorMessage = t("imageUploader.selectImageFile");
        sendToastMessage(t("imageUploader.errorTitle"), errorMessage, "error");
        return;
      }

      // Check file size (30MB limit)
      const maxSize = 30 * 1024 * 1024; // 30MB
      if (file.size > maxSize) {
        const errorMessage = t("imageUploader.fileSizeLimit");
        sendToastMessage(t("imageUploader.errorTitle"), errorMessage, "error");
        return;
      }

      setSelectedImage(file);
      setFileName(file.name);
      setShowCropDialog(true);
    },
    [conceptId, t]
  );

  const handleCropConfirm = useCallback(
    async (croppedBlob: Blob) => {
      if (!selectedImage) return;

      try {
        setShowCropDialog(false);
        setUploadProgress(0);

        // Create file from blob
        const croppedFile = new File([croppedBlob], selectedImage.name, {
          type: "image/jpeg",
        });

        // Get signed URL
        const fileExtension = "jpg"; // Always use jpg for cropped images
        const fileDetails = {
          filename: selectedImage.name.replace(/\.[^/.]+$/, ".jpg"),
          contentType: "image/jpeg",
          extension: fileExtension,
        };

        const signedUrlResponse = await getGenericSignedUploadUrl({
          conceptType,
          conceptId,
          ...fileDetails,
        });

        const { signedUrl, destinationPath, bucketName, processingMetadata } =
          signedUrlResponse;

        // Upload to GCS
        await uploadFileToGCS(
          signedUrl,
          croppedFile,
          "image/jpeg",
          (percentage: number) => {
            setUploadProgress(percentage);
          },
          processingMetadata as any
        );

        // Notify parent on completion
        const fullPath = `https://storage.googleapis.com/${bucketName}/${destinationPath}`;
        onUploadComplete(fullPath);

        // Reset state
        setFileName(null);
        setUploadProgress(null);
        setSelectedImage(null);
      } catch (err: unknown) {
        console.error("Upload process failed:", err);

        // Provide user-friendly error messages
        let userMessage = t("imageUploader.genericError");

        if (err instanceof Error) {
          const errorMsg = err.message.toLowerCase();
          if (
            errorMsg.includes("network error") ||
            errorMsg.includes("connection")
          ) {
            userMessage = t("imageUploader.networkError");
          } else if (
            errorMsg.includes("malformed") ||
            errorMsg.includes("security") ||
            errorMsg.includes("400")
          ) {
            userMessage = t("imageUploader.technicalError");
          } else if (
            errorMsg.includes("413") ||
            errorMsg.includes("too large")
          ) {
            userMessage = t("imageUploader.fileTooLargeError");
          } else if (errorMsg.includes("cors") || errorMsg.includes("policy")) {
            userMessage = t("imageUploader.serviceUnavailableError");
          }
        }

        sendToastMessage(
          t("imageUploader.uploadFailedTitle"),
          userMessage,
          "error"
        );
        setUploadProgress(null);
      }
    },
    [selectedImage, conceptType, conceptId, onUploadComplete, t]
  );

  const handleCropCancel = useCallback(() => {
    setShowCropDialog(false);
    setSelectedImage(null);
    setFileName(null);
  }, []);

  return (
    <>
      <FileDropzone
        onFileSelect={handleFileSelect}
        label={label}
        className={className}
        disabled={disabled}
        uploadProgress={uploadProgress}
        fileName={fileName}
        validateFile={customValidateFile}
      />

      <ImageCropper
        isOpen={showCropDialog}
        onClose={handleCropCancel}
        onConfirm={handleCropConfirm}
        imageFile={selectedImage}
        aspectRatio={aspectRatio}
        maxWidth={maxWidth}
        maxHeight={maxHeight}
        quality={quality}
      />
    </>
  );
};
